/**
 * Analytics API service
 */

import { apiClient } from './client';

// Types
export interface VelocityData {
  sprintId: string;
  sprintName: string;
  startDate: string;
  endDate: string;
  plannedPoints: number;
  completedPoints: number;
  velocity: number;
}

export interface BurndownData {
  date: string;
  idealRemaining: number;
  actualRemaining: number;
  completed: number;
}

export interface CycleTimeData {
  taskId: string;
  taskTitle: string;
  createdAt: string;
  completedAt: string;
  cycleTimeDays: number;
  status: string;
}

export interface CycleTimeStats {
  average: number;
  median: number;
  min: number;
  max: number;
  p50: number;
  p75: number;
  p90: number;
  p95: number;
}

export interface TaskFlowData {
  fromStatus: string;
  toStatus: string;
  count: number;
  percentage: number;
}

export interface TeamPerformance {
  userId: string;
  displayName: string;
  avatarUrl?: string;
  tasksCompleted: number;
  pointsCompleted: number;
  averageCycleTime: number;
  participationRate: number;
}

export interface ProjectMetrics {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  blockedTasks: number;
  totalPoints: number;
  completedPoints: number;
  averageVelocity: number;
  averageCycleTime: number;
  throughput: number;
  healthScore: number;
}

export interface TrendData {
  date: string;
  value: number;
  label?: string;
}

export interface DistributionData {
  label: string;
  value: number;
  percentage: number;
  color?: string;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

// Analytics API functions
export const analyticsApi = {
  /**
   * Get project velocity over time
   */
  getVelocity: async (projectId: string, sprintCount?: number): Promise<VelocityData[]> => {
    const response = await apiClient.get<VelocityData[]>(
      `/analytics/velocity/${projectId}`,
      { params: { sprintCount: sprintCount ?? 6 } }
    );
    return response.data;
  },

  /**
   * Get burndown chart data for a sprint
   */
  getBurndown: async (projectId: string, sprintId?: string): Promise<BurndownData[]> => {
    const params: Record<string, string> = {};
    if (sprintId) params.sprintId = sprintId;

    const response = await apiClient.get<BurndownData[]>(
      `/analytics/burndown/${projectId}`,
      { params }
    );
    return response.data;
  },

  /**
   * Get cycle time data
   */
  getCycleTime: async (projectId: string, range?: DateRange): Promise<{
    data: CycleTimeData[];
    stats: CycleTimeStats;
  }> => {
    const response = await apiClient.get<{ data: CycleTimeData[]; stats: CycleTimeStats }>(
      `/analytics/cycle-time/${projectId}`,
      { params: range }
    );
    return response.data;
  },

  /**
   * Get task flow/transitions data
   */
  getTaskFlow: async (projectId: string, range?: DateRange): Promise<TaskFlowData[]> => {
    const response = await apiClient.get<TaskFlowData[]>(
      `/analytics/task-flow/${projectId}`,
      { params: range }
    );
    return response.data;
  },

  /**
   * Get team performance metrics
   */
  getTeamPerformance: async (projectId: string, range?: DateRange): Promise<TeamPerformance[]> => {
    const response = await apiClient.get<TeamPerformance[]>(
      `/analytics/team-performance/${projectId}`,
      { params: range }
    );
    return response.data;
  },

  /**
   * Get overall project metrics
   */
  getProjectMetrics: async (projectId: string): Promise<ProjectMetrics> => {
    const response = await apiClient.get<ProjectMetrics>(
      `/analytics/metrics/${projectId}`
    );
    return response.data;
  },

  /**
   * Get task completion trend
   */
  getCompletionTrend: async (
    projectId: string,
    range?: DateRange,
    granularity?: 'day' | 'week' | 'month'
  ): Promise<TrendData[]> => {
    const response = await apiClient.get<TrendData[]>(
      `/analytics/completion-trend/${projectId}`,
      { params: { ...range, granularity: granularity ?? 'day' } }
    );
    return response.data;
  },

  /**
   * Get task distribution by status
   */
  getStatusDistribution: async (projectId: string): Promise<DistributionData[]> => {
    const response = await apiClient.get<DistributionData[]>(
      `/analytics/status-distribution/${projectId}`
    );
    return response.data;
  },

  /**
   * Get task distribution by priority
   */
  getPriorityDistribution: async (projectId: string): Promise<DistributionData[]> => {
    const response = await apiClient.get<DistributionData[]>(
      `/analytics/priority-distribution/${projectId}`
    );
    return response.data;
  },

  /**
   * Get task distribution by assignee
   */
  getAssigneeDistribution: async (projectId: string): Promise<DistributionData[]> => {
    const response = await apiClient.get<DistributionData[]>(
      `/analytics/assignee-distribution/${projectId}`
    );
    return response.data;
  },

  /**
   * Get task distribution by label
   */
  getLabelDistribution: async (projectId: string): Promise<DistributionData[]> => {
    const response = await apiClient.get<DistributionData[]>(
      `/analytics/label-distribution/${projectId}`
    );
    return response.data;
  },

  /**
   * Export analytics report
   */
  exportReport: async (
    projectId: string,
    format: 'pdf' | 'csv' | 'xlsx',
    sections?: string[]
  ): Promise<Blob> => {
    const response = await apiClient.post<Blob>(
      `/analytics/export/${projectId}`,
      { format, sections },
      { responseType: 'blob' }
    );
    return response.data;
  },
};
